import "@/sim/sim.css";
import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CreditCard, Lock, Check, Loader2, Radar as RadarIcon, ShieldCheck } from "lucide-react";
import { startCardSetup, simLogout } from "@/sim/lib";
import { cn } from "@/lib/utils";

const PLANS = {
  year: { label: "Annual", price: 990, unit: "/year", per: "$82.50/mo billed yearly", badge: "2 MONTHS FREE", save: "Save $198 vs monthly" },
  month: { label: "Monthly", price: 99, unit: "/month", per: "Billed every month", badge: null, save: null },
};

export default function SimBilling() {
  const [loading, setLoading] = useState(false);
  const [planInterval, setPlanInterval] = useState("year");
  const plan = PLANS[planInterval];

  const perks = [
    "3 full simulations free — no charge today",
    `Then ${"$" + plan.price}${plan.unit} for unlimited runs`,
    "10 synthetic users scrutinize any URL in ~75s",
    "Cancel anytime — your card is stored securely by Stripe",
  ];

  const addCard = async () => {
    setLoading(true);
    try {
      const { url } = await startCardSetup(window.location.origin, planInterval);
      window.location.href = url;
    } catch (e) {
      toast.error(e.message || "Could not open secure card setup");
      setLoading(false);
    }
  };

  return (
    <div className="sim-root sim-grid-bg min-h-screen flex items-center justify-center px-5 py-12" data-testid="sim-billing">
      <motion.div
        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
        className="sim-card-elevated rounded-2xl p-8 max-w-md w-full"
      >
        <div className="flex items-center gap-2.5 mb-6">
          <div className="w-8 h-8 rounded-md bg-[#00F0FF]/10 border border-[#00F0FF]/30 flex items-center justify-center">
            <RadarIcon className="w-4 h-4 text-[#00F0FF]" />
          </div>
          <span className="sim-head text-[15px] font-extrabold text-[#F5F5F5] tracking-tight">
            sim<span className="text-[#00F0FF]">.</span>roas-ai
          </span>
        </div>

        <div className="sim-mono inline-flex items-center gap-2 text-[11px] text-[#00F0FF] border border-[#00F0FF]/25 bg-[#00F0FF]/5 rounded-full px-3 py-1 mb-5">
          <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF] animate-pulse" /> START YOUR FREE TRIAL
        </div>

        <h1 className="sim-head text-3xl font-extrabold text-[#F5F5F5] leading-tight">
          Add a card to unlock<br />your 3 free simulations
        </h1>
        <p className="text-[#D4D4D8] text-sm mt-3">
          No charge today. Run <span className="text-[#F5F5F5] font-semibold">3 simulations free</span> — after that you'll be charged
          <span className="text-[#00F0FF] font-semibold"> ${plan.price}{plan.unit}</span> for unlimited runs.
        </p>

        {/* Plan selector */}
        <div className="grid grid-cols-2 gap-2.5 mt-6" data-testid="sim-plan-selector">
          {["year", "month"].map((key) => {
            const p = PLANS[key];
            const selected = planInterval === key;
            return (
              <button
                key={key}
                data-testid={`sim-plan-${key}`}
                onClick={() => setPlanInterval(key)}
                className={cn(
                  "relative text-left rounded-xl border p-3.5 transition-colors",
                  selected ? "border-[#00F0FF] bg-[#00F0FF]/5" : "border-[#27272A] hover:border-[#3f3f46]"
                )}
              >
                {p.badge && (
                  <span className="absolute -top-2 right-2 sim-mono text-[10px] font-bold bg-[#10B981] text-[#0A0A0A] px-1.5 py-0.5 rounded">
                    {p.badge}
                  </span>
                )}
                <div className="flex items-center gap-1.5">
                  <span className={cn("w-3.5 h-3.5 rounded-full border flex items-center justify-center shrink-0",
                    selected ? "border-[#00F0FF] bg-[#00F0FF]" : "border-[#8E929C]")}>
                    {selected && <Check className="w-2.5 h-2.5 text-[#0A0A0A]" />}
                  </span>
                  <span className="sim-mono text-[11px] text-[#C2C5CE] uppercase tracking-wide">{p.label}</span>
                </div>
                <p className="sim-head text-xl font-extrabold text-[#F5F5F5] mt-1.5">
                  ${p.price}<span className="text-xs font-semibold text-[#9AA0AC]">{p.unit}</span>
                </p>
                <p className="sim-mono text-[10px] text-[#C2C5CE] mt-0.5">{p.save || p.per}</p>
              </button>
            );
          })}
        </div>

        <div className="space-y-2.5 mt-6 mb-7">
          {perks.map((p) => (
            <div key={p} className="flex items-start gap-2.5">
              <div className="w-4 h-4 rounded-full bg-[#10B981]/15 flex items-center justify-center mt-0.5 shrink-0">
                <Check className="w-2.5 h-2.5 text-[#10B981]" />
              </div>
              <span className="text-[#D4D4D8] text-sm">{p}</span>
            </div>
          ))}
        </div>

        <button
          data-testid="sim-add-card-btn"
          onClick={addCard}
          disabled={loading}
          className="sim-tracing-btn w-full h-12 rounded-lg bg-[#00F0FF] text-[#0A0A0A] font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#33f3ff] transition-colors disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
          {loading ? "Opening secure checkout…" : "Add card & start free trial"}
        </button>

        <p className="text-center sim-mono text-[10px] text-[#C2C5CE] mt-2.5">
          After your 3 free sims: ${plan.price}{plan.unit}, auto-charged to your saved card
        </p>

        <div className="flex items-center justify-center gap-1.5 mt-4 sim-mono text-[11px] text-[#9AA0AC]">
          <Lock className="w-3 h-3" /> Secured by Stripe
          <span className="mx-1">·</span>
          <ShieldCheck className="w-3 h-3" /> PCI-compliant
        </div>

        <button
          data-testid="sim-billing-signout"
          onClick={async () => { await simLogout(); window.location.href = "/sim"; }}
          className="w-full text-center mt-4 sim-mono text-[11px] text-[#9AA0AC] hover:text-[#F5F5F5] transition-colors"
        >
          Not you? Sign out
        </button>
      </motion.div>
    </div>
  );
}
